<div class="h-full flex flex-col" [class]="floatingPageClasses()">
  <div
    class="flex items-center justify-between gap-lg"
    [class.mb-lg]="floating()">
    <h5 class="text-neutral-900 font-semibold text-2xl">
      {{ 'group_list_screen_title' | transloco }}
    </h5>

    @if (floating()) {
      <ng-container [ngTemplateOutlet]="tableActionSectionRef" />
    }
  </div>

  <section
    [class]="floating() ? 'floating-card p-2xl flex flex-col' : 'contents'"
    data-testid="group-list-section">
    <!-- UI_Version_2: no title or count row; the search is in the table toolbar -->
    @if (!floating() || !searchInToolbar()) {
      <div
        class="flex justify-between items-center"
        [class]="
          floating() ? 'mb-lg' : 'py-lg my-2xl border-b border-neutral-200'
        ">
        <div class="flex items-center gap-lg">
          @if (!floating()) {
            <h5 class="text-neutral-900 font-semibold text-lg">
              {{ 'group_list_screen_subtitle' | transloco }}
            </h5>

            <lib-badge
              label="{{ totalItems() }}"
              [badgeClass]="'px-sm border-none text-sm font-medium bg-primary-50 text-primary-600'" />
          }
        </div>

        <!-- Tables_new_design shows the search in the table toolbar instead -->
        @if (!searchInToolbar()) {
          <ng-container [ngTemplateOutlet]="groupsSearch" />
        }
      </div>
    }

    <ng-template #groupsSearch>
      <lib-search-field
        class="shrink-0"
        [searchInputWidth]="searchInputWidth"
        [placeholder]="'search_by_name_placeholder'"
        (searchKeywordEmitter)="onSearchTermChange($event)" />
    </ng-template>

    <ng-template #tableActionSectionRef>
      <a
        class="flex items-center bg-primary-500 text-white font-medium text-sm py-sm px-2xl border-none rounded-md gap-sm"
        [routerLink]="[`${ADMIN_ROUTES.GROUPS.FORM}` | tenantPath: 'admin']"
        id="group_list_screen_new_group_button">
        <lib-add-icon [iconClass]="'size-5 text-white'" />

        <span class="hidden md:block">
          {{ 'group_list_screen_new_user_button' | transloco }}
        </span>
      </a>
    </ng-template>

    <lib-table
      [tableClass]="'min-h-[calc(100vh-25rem)] h-auto w-full whitespace-nowrap'"
      [id]="'group_table'"
      [dataSource]="groupList()"
      [loading]="isLoading()"
      [columns]="columns()"
      [viewCount]="totalItems()"
      [noDataTitle]="'table_no_data_title' | transloco"
      [noDataDescription]="'section_no_data_description' | transloco"
      [bodyTemplateRef]="[number, name, total_users, status, action]"
      [titleBottomBorder]="false"
      [tableActionSectionRef]="floating() ? null : tableActionSectionRef"
      [searchTemplateRef]="searchInToolbar() ? groupsSearch : null"
      [tableFilterTabs]="tableFilterTabs()"
      [lastColumnAlignment]="'justify-end'"
      (handleViewIconClick)="viewGroupDetails($event)"
      (handleTableFilterTab)="handleTableFilterTab($event)"
      (handleSortColumn)="handleSortColumn($event)">
      <ng-template #number let-row let-i="i">
        {{ (currentPage() - 1) * currentSize() + (i + 1) }}
      </ng-template>

      <ng-template #name let-row>
        <div class="flex items-center gap-md">
          @if (row.image) {
            <img
              [src]="row.image.url"
              [alt]="row.image.name"
              width="28px"
              height="28px"
              class="rounded-sm" />
          } @else {
            <span
              class="rounded-sm bg-primary-50 text-primary-500 flex justify-center items-center size-7">
              {{ row.name | avatar }}
            </span>
          }
          <span class="font-medium text-xs">
            {{ row.name | truncate }}
          </span>
        </div>
      </ng-template>

      <ng-template #total_users let-row>
        {{ row.users_count }}
      </ng-template>

      <ng-template #status let-row>
        <lib-badge
          [label]="row.state.label"
          [badgeClass]="
            'px-sm border-none text-xs font-medium text-white ' +
            (row.state.value | status)
          " />
      </ng-template>

      <ng-template #action let-row>
        <lib-action-list
          [menuItems]="getStatusActionList(row.state.value, row.id)"
          (itemClick)="handleAction($event, row)" />
      </ng-template>
    </lib-table>

    @if (groupList().length > 0) {
      <lib-paginator
        class="mt-2xl"
        [sizeOptionsLabel]="
          'pagination_number_of_rows_per_page_label' | transloco
        "
        [nextBtnLabel]="'pagination_next_button_label' | transloco"
        [previousBtnLabel]="'pagination_previous_button_label' | transloco"
        [currentPage]="currentPage()"
        [totalPages]="totalPages()"
        [totalItems]="totalItems()"
        [showChangeSize]="true"
        [sizeOptions]="pageSizeOptions"
        (goTo)="handlePagination($event)"
        (next)="handlePagination($event)"
        (previous)="handlePagination($event)"
        (handleChangeSize)="changeSize($event)"
        [currentSize]="currentSize()" />
    }
  </section>
</div>

<ng-template #deactivate>
  <div class="text-center">
    <div
      class="inline-flex justify-center items-center bg-destructive-50 p-sm rounded-full mb-xl w-[4rem] h-[4rem]">
      <lib-close-square-icon
        viewBox="0 0 30 30"
        width="27"
        height="27"
        iconClass="text-destructive-500" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm">
    {{ 'deactivate_group_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'deactivate_group_modal_description' | transloco }}
    <span class="font-medium text-primary-500">
      {{ 'deactivate_group_modal_highlighted_description' | transloco }}
    </span>
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      [class.opacity-30]="isGroupStateLoading()"
      [disabled]="isGroupStateLoading()"
      (click)="deactivateGroup()"
      class="bg-destructive-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'deactivate_group_modal_deactivate_group_button' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'deactivate_group_modal_no_keep_it_button' | transloco }}
    </button>
  </div>
</ng-template>

<ng-template #activate>
  <div class="text-center">
    <div
      class="inline-flex justify-center items-center bg-primary-50 p-sm rounded-full mb-xl img-width-container">
      <lib-tick-square-icon iconClass="size-8 text-primary-500" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm">
    {{ 'activate_group_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'activate_group_modal_description' | transloco }}
    <span class="font-medium text-primary-500 block">
      {{ 'activate_group_modal_highlighted_description' | transloco }}
    </span>
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      [disabled]="isGroupStateLoading()"
      (click)="activateGroup()"
      class="bg-primary-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'activate_group_modal_activate_group_button' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'activate_group_modal_cancel_button' | transloco }}
    </button>
  </div>
</ng-template>

<ng-template #deleteRecord>
  <div class="text-center">
    <div
      class="inline-flex justify-center items-center bg-destructive-50 p-sm rounded-full mb-xl w-[4rem] h-[4rem]">
      <lib-delete-icon [iconClass]="'size-8 text-destructive-500'" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm">
    {{ 'delete_group_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'delete_group_modal_description' | transloco }}
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      [class.opacity-30]="isGroupStateLoading()"
      [disabled]="isGroupStateLoading()"
      (click)="deleteGroup()"
      class="bg-destructive-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'delete_group_modal_delete_group_button' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'delete_group_modal_cancel_button' | transloco }}
    </button>
  </div>
</ng-template>
